import { Trans } from "@lingui/macro";
import { useCallback } from "react";

import { MULTICHAIN_SOURCE_TO_SETTLEMENTS_MAPPING } from "config/multichain";
import { useGmxAccountSettlementChainId } from "context/GmxAccountContext/hooks";
import { useEmptyGmxAccounts } from "domain/multichain/useEmptyGmxAccounts";
import { useChainId } from "lib/chains";
import { formatUsd } from "lib/numbers";
import { EMPTY_OBJECT } from "lib/objects";
import { getChainName, SettlementChainId } from "sdk/configs/chains";

import { ColorfulBanner, ColorfulButtonLink } from "components/ColorfulBanner/ColorfulBanner";
import { useAvailableToTradeAssetMultichainRequest } from "components/GmxAccountModal/hooks";

import WarnIcon from "img/ic_warn.svg?react";

export function SettlementChainWarningContainer() {
  const { chainId: fallbackChainId, srcChainId } = useChainId();

  const [settlementChainId, setGmxAccountSettlementChainId] = useGmxAccountSettlementChainId();

  const settlementChains = srcChainId ? MULTICHAIN_SOURCE_TO_SETTLEMENTS_MAPPING[srcChainId] : undefined;

  const { emptyGmxAccounts } = useEmptyGmxAccounts(settlementChains);

  const isCurrentSettlementChainEmpty = emptyGmxAccounts?.[settlementChainId] === true;

  const anyNonEmptyGmxAccountChainId = Object.entries(emptyGmxAccounts ?? EMPTY_OBJECT)
    .filter(([chainId, isEmpty]) => !isEmpty && chainId !== settlementChainId.toString())
    .map(([chainId]) => Number(chainId) as SettlementChainId)
    .at(0);

  const { gmxAccountUsd } = useAvailableToTradeAssetMultichainRequest(
    anyNonEmptyGmxAccountChainId ?? fallbackChainId,
    srcChainId
  );

  const handleNetworkSwitch = useCallback(() => {
    if (!anyNonEmptyGmxAccountChainId) {
      return;
    }

    setGmxAccountSettlementChainId(anyNonEmptyGmxAccountChainId);
  }, [setGmxAccountSettlementChainId, anyNonEmptyGmxAccountChainId]);

  if (!anyNonEmptyGmxAccountChainId || !isCurrentSettlementChainEmpty) {
    return null;
  }

  return (
    <ColorfulBanner color="yellow" icon={WarnIcon} className="text-body-small">
      <Trans>
        Settlement network changed to {getChainName(settlementChainId)}, but {formatUsd(gmxAccountUsd)} remains in your{" "}
        {getChainName(anyNonEmptyGmxAccountChainId)} GMX Account
      </Trans>

      <ColorfulButtonLink color="yellow" onClick={handleNetworkSwitch}>
        <Trans>Change to {getChainName(anyNonEmptyGmxAccountChainId)}</Trans>
      </ColorfulButtonLink>
    </ColorfulBanner>
  );
}
